/* eslint-disable no-continue */
/* eslint-disable no-await-in-loop */
import localforage from 'localforage';
import {useCallback, useMemo} from 'react';

export type IDBTableList =
  | 'LocalCompositions'
  | 'LocalWritePreparations'
  | 'LocalQuestionnaires';

function useIndexedDB<TableType>(tableName: IDBTableList) {
  const IDBTable = useMemo(
    () =>
      localforage.createInstance({
        name: 'studentIDB',
        storeName: tableName,
        version: 1,
      }),
    [tableName],
  );

  // Retry logic for transient errors (aborted connection)
  async function withRetry<T>(fn: () => Promise<T>): Promise<T | null> {
    let attempt = 0;
    const maxAttempts = 2;
    while (attempt <= maxAttempts) {
      try {
        return await fn();
      } catch (error: any) {
        // Retry for aborted errors due to concurrency issues
        if (error?.message?.includes('aborted') && attempt < maxAttempts) {
          attempt++;
          continue;
        }
        // Remove the oldest entry and retry for full disk
        if (
          error?.message?.includes('Quota') ||
          error?.message?.includes('full')
        ) {
          await IDBTable.iterate<TableType, unknown>((_, storeKey, index) => {
            if (index === 0) {
              IDBTable.removeItem(storeKey);
            }
          });
          attempt++;
          continue;
        }
        break;
      }
    }
    return null;
  }

  const getItem = useCallback(
    (key?: string | number) =>
      withRetry(async () => {
        await IDBTable.ready();

        const item = await IDBTable.getItem<TableType>(String(key));
        return item;
      }),
    [IDBTable],
  );

  const getAll = useCallback(
    async (key = '') =>
      withRetry(async () => {
        const IDBTableItems: TableType[] = [];
        await IDBTable.ready();

        await IDBTable.iterate<TableType, unknown>((storeValue, storeKey) => {
          if (storeKey.includes(key)) {
            IDBTableItems.push(storeValue);
          }
        });

        return IDBTableItems;
      }),
    [IDBTable],
  );

  const getAllSortedBy = useCallback(
    async (key = '', sortParam: keyof TableType) => {
      const IDBTableItems = await getAll(key);
      if (!IDBTableItems) return [];

      return IDBTableItems.sort(
        (item1, item2) => Number(item2[sortParam]) - Number(item1[sortParam]),
      );
    },
    [getAll],
  );

  const updateItem = useCallback(
    async (key = '', value: Partial<TableType>) =>
      withRetry(async () => {
        const IDBTableItem = await IDBTable.getItem<TableType>(key);

        IDBTable.setItem(key, {...IDBTableItem, ...value});
      }),
    [IDBTable],
  );

  const removeList = useCallback(
    async (keys: string[] | undefined) =>
      withRetry(async () => {
        await IDBTable.ready();
        if (!keys) return;

        await Promise.all(
          keys.map(async (key) => {
            const keysToRemove: string[] = [];
            await IDBTable.iterate<TableType, unknown>((_, storeKey) => {
              if (storeKey.includes(key)) {
                keysToRemove.push(storeKey);
              }
            });

            await Promise.all(
              keysToRemove.map(async (storeKey) => {
                IDBTable.removeItem(storeKey);
              }),
            );
          }),
        );
      }),
    [IDBTable],
  );

  const setItem = useCallback(
    async (key?: string, value?: TableType) =>
      withRetry(async () => {
        await IDBTable.ready();

        if (key && value) {
          return IDBTable.setItem(key, {...value});
        }

        return null;
      }),
    [IDBTable],
  );

  return {
    ...IDBTable,
    setItem,
    getItem,
    updateItem,
    getAll,
    getAllSortedBy,
    removeList,
  };
}

export default useIndexedDB;
